import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_SCORECARD_SEASON_YEARS,
  SCORECARD_LIVE_SEASON_YEAR,
  extractAvailableYears,
  extractScorecardData,
  formatSeasonYearOptionLabel,
  findZilaWiseItem,
  getComparisonYearFromScorecardData,
  getScorecardEndpoint,
  getScorecardErrorMessage,
  getScorecardQueryString,
  isLiveSeasonYear,
  mergeSeasonYearOptions,
} from "../utils/scorecardApi";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";

const EID_DAY_OPTIONS = [
  { label: "All Days", value: "all" },
  { label: "Eid Day 1", value: "EID_DAY_1" },
  { label: "Eid Day 2", value: "EID_DAY_2" },
  { label: "Eid Day 3", value: "EID_DAY_3" },
  { label: "Eid Day 4", value: "EID_DAY_4" },
];

const ANIMAL_KEYS = [
  { key: "cow", label: "COW", color: "#1d4ed8" },
  { key: "goat", label: "GOAT", color: "#15803d" },
  { key: "sheep", label: "SHEEP", color: "#7c3aed" },
  { key: "camel", label: "CAMEL", color: "#c2410c" },
  { key: "buffalo", label: "BUFFALO", color: "#92400e" },
];

const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }
  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }

  return cleaned;
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getCount = (row, keys) => {
  for (const key of keys) {
    if (row && row[key] != null) {
      return toNumber(row[key]);
    }
  }
  return 0;
};

const getPreviousAnimalCount = (row, animalKey) =>
  getCount(row, [
    `previous_${animalKey}`,
    `previous_${animalKey}_count`,
    `prev_year_${animalKey}`,
    `prev_year_${animalKey}_count`,
    `${animalKey}_previous`,
    `${animalKey}_prev`,
    `prev_${animalKey}`,
    `prev_${animalKey}_count`,
  ]);

const getComparisonCountSection = (comparison) => {
  if (!comparison || typeof comparison !== "object") {
    return null;
  }

  for (const key of ["previous_counts", "prev_year_counts", "counts", "previous"]) {
    const section = comparison[key];
    if (section && typeof section === "object" && !Array.isArray(section)) {
      if (
        hasCountFields(section) ||
        ANIMAL_KEYS.some(({ key: animalKey }) =>
          Object.prototype.hasOwnProperty.call(section, `${animalKey}_count`)
        )
      ) {
        return section;
      }
    }
  }

  return null;
};

const fillPreviousAnimalsFromComparisonCounts = (normalized, comparison) => {
  if (!comparison || typeof comparison !== "object") {
    return;
  }

  const hasExplicitPreviousAnimals = ANIMAL_KEYS.some(
    ({ key }) => normalized[`previous_${key}`] > 0
  );
  if (hasExplicitPreviousAnimals) {
    return;
  }

  const sections = [comparison, getComparisonCountSection(comparison)].filter(Boolean);

  for (const section of sections) {
    for (const { key } of ANIMAL_KEYS) {
      if (normalized[`previous_${key}`] > 0) {
        continue;
      }
      const fromSection = getCount(section, [
        `previous_${key}_count`,
        `previous_${key}`,
        `prev_year_${key}_count`,
        `${key}_count`,
        key,
      ]);
      if (fromSection > 0) {
        normalized[`previous_${key}`] = fromSection;
      }
    }
  }
};

const hasPreviousAnimalCounts = (summary) =>
  ANIMAL_KEYS.some(({ key }) => (summary?.[`previous_${key}`] ?? 0) > 0);

const applyPreviousAnimalFallback = (normalized) => {
  if (!normalized || hasPreviousAnimalCounts(normalized)) {
    return normalized;
  }

  const previousTotal = normalized.previous_total ?? 0;
  const total = normalized.total ?? 0;

  if (previousTotal > 0 && total > 0) {
    for (const { key } of ANIMAL_KEYS) {
      normalized[`previous_${key}`] = Math.round((normalized[key] / total) * previousTotal);
    }
  }

  return normalized;
};

const mergeChartSummaries = (primary, aggregated) => {
  if (!aggregated) {
    return applyPreviousAnimalFallback({ ...primary });
  }

  const merged = { ...primary };

  if ((merged.previous_total ?? 0) <= 0 && (aggregated.previous_total ?? 0) > 0) {
    merged.previous_total = aggregated.previous_total;
  }

  if (!hasPreviousAnimalCounts(merged) && hasPreviousAnimalCounts(aggregated)) {
    for (const { key } of ANIMAL_KEYS) {
      merged[`previous_${key}`] = aggregated[`previous_${key}`] ?? 0;
    }
  }

  const primaryAllCurrentZero = ANIMAL_KEYS.every(({ key }) => (merged[key] ?? 0) === 0);
  if (primaryAllCurrentZero) {
    for (const { key } of ANIMAL_KEYS) {
      merged[key] = aggregated[key] ?? 0;
    }
    merged.total = aggregated.total ?? 0;
  }

  return applyPreviousAnimalFallback(merged);
};

const aggregateSummaryFromZilaWise = (data) => {
  const section = data?.zila_wise || data?.zilaWise;
  if (!Array.isArray(section) || section.length === 0) {
    return null;
  }

  const totals = {
    cow: 0,
    goat: 0,
    sheep: 0,
    camel: 0,
    buffalo: 0,
    total: 0,
    previous_cow: 0,
    previous_goat: 0,
    previous_sheep: 0,
    previous_camel: 0,
    previous_buffalo: 0,
    previous_total: 0,
  };

  for (const item of section) {
    const counts = item?.counts && typeof item.counts === "object" ? item.counts : {};
    const comparison = mergeComparisonWithCountSections(
      item?.comparison && typeof item.comparison === "object" ? item.comparison : {}
    );
    const previousCounts = getPreviousCountsSection(item);

    for (const { key } of ANIMAL_KEYS) {
      totals[key] += getCount(counts, [`${key}_count`, key]);
      let previous = 0;
      if (previousCounts) {
        previous = getCount(previousCounts, [`${key}_count`, key]);
      }
      if (previous <= 0) {
        previous = getPreviousAnimalCount(comparison, key);
      }
      if (previous <= 0) {
        previous = getCount(comparison, [`${key}_count`, key]);
      }
      totals[`previous_${key}`] += previous;
    }

    totals.total += getCount(counts, ["total_count", "total", "count"]);
    totals.previous_total += getCount(previousCounts, ["total_count", "total", "count"]);
    if (totals.previous_total <= 0) {
      totals.previous_total += getCount(comparison, [
        "previous_total",
        "prev_year_total",
        "prev_total",
      ]);
    }
  }

  return applyPreviousAnimalFallback(
    normalizeSummaryRow(
      {
        cow_count: totals.cow,
        goat_count: totals.goat,
        sheep_count: totals.sheep,
        camel_count: totals.camel,
        buffalo_count: totals.buffalo,
        total_count: totals.total,
      },
      { previous_total: totals.previous_total }
    )
  );
};

const getPreviousYearTotalsSection = (data) => {
  const section = data?.previous_year_totals ?? data?.previousYearTotals;
  return section && typeof section === "object" && !Array.isArray(section) ? section : null;
};

const getPreviousCountsSection = (source) => {
  const section = source?.previous_counts ?? source?.previousCounts;
  return section && typeof section === "object" && !Array.isArray(section) ? section : null;
};

/** Single-zila chart: `zila_year_bars` → current vs previous year animal counts. */
const summaryFromZilaYearBars = (data) => {
  const bars = data?.zila_year_bars ?? data?.zilaYearBars;
  if (!Array.isArray(bars) || bars.length === 0) {
    return null;
  }

  const selectedYear = Number(data?.selected_year);
  const previousYear = Number(data?.previous_year);

  let currentBar = Number.isFinite(selectedYear)
    ? bars.find((bar) => Number(bar?.year) === selectedYear)
    : null;
  let previousBar = Number.isFinite(previousYear)
    ? bars.find((bar) => Number(bar?.year) === previousYear)
    : null;

  if (!currentBar) {
    currentBar = bars[0];
  }
  if (!previousBar) {
    previousBar = bars.find((bar) => bar !== currentBar) ?? bars[1] ?? bars[0];
  }

  const currentCounts =
    currentBar?.counts && typeof currentBar.counts === "object" ? currentBar.counts : {};
  const previousCounts =
    previousBar?.counts && typeof previousBar.counts === "object" ? previousBar.counts : {};
  const overallComparison = mergeComparisonWithCountSections(
    data?.total_comparison && typeof data.total_comparison === "object" ? data.total_comparison : {}
  );

  return normalizeSummaryRow(currentCounts, overallComparison, previousCounts);
};

const getCurrentTotalsSection = (data) => {
  const section = data?.total;
  return section && typeof section === "object" && !Array.isArray(section) ? section : null;
};

const applyPreviousYearTotalsSection = (normalized, previousYearTotals) => {
  if (!previousYearTotals || typeof previousYearTotals !== "object") {
    return;
  }

  for (const { key } of ANIMAL_KEYS) {
    normalized[`previous_${key}`] = getCount(previousYearTotals, [`${key}_count`, key]);
  }

  const previousTotal = getCount(previousYearTotals, ["total_count", "total", "count"]);
  if (previousTotal > 0) {
    normalized.previous_total = previousTotal;
  }
};

const normalizeSummaryRow = (row, comparison = {}, previousYearTotals = null) => {
  const merged = row && typeof row === "object" ? row : {};
  const comparisonRow = comparison && typeof comparison === "object" ? comparison : {};
  const total =
    getCount(merged, ["total_count", "total", "count", "current_total"]) ||
    getCount(comparisonRow, ["current_total"]);
  const previousTotal =
    getCount(comparisonRow, ["previous_total", "prev_year_total", "prev_total"]) ||
    getCount(merged, ["previous_total"]);
  const percentFromApi = getCount(comparisonRow, ["percent"]) || getCount(merged, ["percent"]);
  const diff = getCount(comparisonRow, ["diff"]) || getCount(merged, ["diff"]);

  const normalized = {
    total,
    previous_total: previousTotal,
    percent: percentFromApi,
    diff,
    cow: getCount(merged, ["cow_count", "cow"]),
    goat: getCount(merged, ["goat_count", "goat"]),
    sheep: getCount(merged, ["sheep_count", "sheep"]),
    camel: getCount(merged, ["camel_count", "camel"]),
    buffalo: getCount(merged, ["buffalo_count", "buffalo"]),
    previous_cow:
      getPreviousAnimalCount(comparisonRow, "cow") || getPreviousAnimalCount(merged, "cow"),
    previous_goat:
      getPreviousAnimalCount(comparisonRow, "goat") || getPreviousAnimalCount(merged, "goat"),
    previous_sheep:
      getPreviousAnimalCount(comparisonRow, "sheep") || getPreviousAnimalCount(merged, "sheep"),
    previous_camel:
      getPreviousAnimalCount(comparisonRow, "camel") || getPreviousAnimalCount(merged, "camel"),
    previous_buffalo:
      getPreviousAnimalCount(comparisonRow, "buffalo") ||
      getPreviousAnimalCount(merged, "buffalo"),
    raw: merged,
    comparison: comparisonRow,
  };

  applyPreviousYearTotalsSection(normalized, previousYearTotals);
  fillPreviousAnimalsFromComparisonCounts(normalized, comparisonRow);

  return applyPreviousAnimalFallback(normalized);
};

const hasCountFields = (section) => {
  if (!section || typeof section !== "object" || Array.isArray(section)) {
    return false;
  }

  return [
    "total_count",
    "cow_count",
    "goat_count",
    "sheep_count",
    "camel_count",
    "buffalo_count",
  ].some((key) => Object.prototype.hasOwnProperty.call(section, key));
};

const normalizeScoreTableRow = (row, labelKey = "total") => ({
  ...((row?.counts && typeof row.counts === "object" ? row.counts : {})),
  ...(row && typeof row === "object" ? row : {}),
  label:
    row?.label ||
    row?.name ||
    row?.tannery_name ||
    row?.tanneryName ||
    row?.zila ||
    row?.zila_name ||
    row?.district ||
    row?.elaqa ||
    row?.elaqa_name ||
    row?.area ||
    labelKey,
  total: getCount(row, ["total_count", "total", "count"]),
  cow: getCount(row, ["cow_count", "cow"]),
  goat: getCount(row, ["goat_count", "goat"]),
  sheep: getCount(row, ["sheep_count", "sheep"]),
  camel: getCount(row, ["camel_count", "camel"]),
  buffalo: getCount(row, ["buffalo_count", "buffalo"]),
});

const toScoreRows = (section, fallbackLabel) => {
  if (Array.isArray(section)) {
    return section.map((item) => {
      const comparison =
        item && typeof item === "object" && item.comparison && typeof item.comparison === "object"
          ? item.comparison
          : {};
      const mergedRow =
        item && typeof item === "object" && item.counts && typeof item.counts === "object"
          ? { ...item.counts, ...item, ...comparison }
          : item;
      return normalizeScoreTableRow(mergedRow, fallbackLabel);
    });
  }
  if (section && typeof section === "object") {
    if (hasCountFields(section)) {
      return [normalizeScoreTableRow(section, fallbackLabel)];
    }

    return Object.entries(section).map(([key, value]) =>
      normalizeScoreTableRow(
        {
          ...(typeof value === "object" && value !== null ? value : { total: value }),
          label: key,
        },
        fallbackLabel
      )
    );
  }
  return [];
};

const getZilaRowTitle = (row) =>
  String(row?.zila_name || row?.zila || row?.district || row?.label || row?.name || "").trim() || "—";

const getZilaAreaTag = (row) => {
  const tag = String(
    row?.elaqa_name || row?.elaqa || row?.area_name || row?.region_name || row?.sub_area || ""
  ).trim();
  return tag || null;
};

const summaryFromZilaWiseItem = (item) => {
  const counts = item?.counts && typeof item.counts === "object" ? item.counts : {};
  const comparison = mergeComparisonWithCountSections(
    item?.comparison && typeof item.comparison === "object" ? item.comparison : {}
  );
  const previousYearTotals =
    getPreviousCountsSection(item) || getPreviousYearTotalsSection(item);
  return normalizeSummaryRow(counts, comparison, previousYearTotals);
};

const mergeComparisonWithCountSections = (comparison) => {
  if (!comparison || typeof comparison !== "object") {
    return {};
  }
  const nestedCounts = getComparisonCountSection(comparison);
  return nestedCounts ? { ...comparison, ...nestedCounts } : comparison;
};

const firstSummaryRowFromPayload = (data) => {
  const currentTotals = getCurrentTotalsSection(data);
  const previousYearTotals = getPreviousYearTotalsSection(data);

  if (currentTotals || previousYearTotals) {
    const overallComparison = mergeComparisonWithCountSections(
      data?.total_comparison && typeof data.total_comparison === "object" ? data.total_comparison : {}
    );
    return normalizeSummaryRow(currentTotals || {}, overallComparison, previousYearTotals);
  }

  const overallComparison = mergeComparisonWithCountSections(
    data?.total_comparison && typeof data.total_comparison === "object" ? data.total_comparison : {}
  );
  const overallSection = data?.summary || data?.overall || data?.totals;

  let baseRow = null;
  let itemComparison = {};

  if (overallSection) {
    if (Array.isArray(overallSection)) {
      const first = overallSection[0];
      if (first && typeof first === "object") {
        itemComparison = mergeComparisonWithCountSections(
          first.comparison && typeof first.comparison === "object" ? first.comparison : {}
        );
        baseRow =
          first.counts && typeof first.counts === "object"
            ? { ...first.counts, ...first }
            : { ...first };
      }
    } else if (typeof overallSection === "object") {
      if (hasCountFields(overallSection)) {
        baseRow = overallSection;
      } else {
        const entries = Object.entries(overallSection).filter(
          ([key]) => key !== "zila_wise" && key !== "zilaWise" && key !== "total_comparison"
        );
        if (entries.length > 0) {
          const [, value] = entries[0];
          baseRow = typeof value === "object" && value !== null ? value : { total: value };
        }
      }
    }
  }

  if (
    baseRow ||
    getCount(overallComparison, ["previous_total"]) > 0 ||
    getCount(itemComparison, ["previous_total"]) > 0
  ) {
    return normalizeSummaryRow(baseRow || {}, { ...overallComparison, ...itemComparison });
  }

  if (overallComparison && typeof overallComparison === "object") {
    return normalizeSummaryRow(
      {
        total_count: getCount(overallComparison, ["current_total", "total_count", "total"]),
      },
      overallComparison
    );
  }

  return normalizeSummaryRow({}, overallComparison);
};

/** Org-wide: `total` + `previous_year_totals`. Single zila: `zila_year_bars` (then `zila_wise` fallback). */
const scorecardSummaryFromPayload = (data, { zila } = {}) => {
  const zilaName = String(zila || "").trim();
  if (zilaName && zilaName !== "all") {
    const fromYearBars = summaryFromZilaYearBars(data);
    if (fromYearBars) {
      return fromYearBars;
    }

    const zilaItem = findZilaWiseItem(data, zilaName);
    if (zilaItem) {
      return applyPreviousAnimalFallback(summaryFromZilaWiseItem(zilaItem));
    }
  }

  const overall = firstSummaryRowFromPayload(data);
  if (hasPreviousAnimalCounts(overall)) {
    return overall;
  }

  const aggregated = aggregateSummaryFromZilaWise(data);
  return mergeChartSummaries(overall, aggregated);
};

const parsePayload = async (response) => {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const formatInt = (n) => Math.round(Number(n) || 0).toLocaleString();

const animalLabelShort = (label) =>
  String(label || "")
    .charAt(0)
    .toUpperCase() + String(label || "").slice(1).toLowerCase();

const formatAxisTick = (value) => {
  const n = Math.round(Number(value) || 0);
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (n >= 10_000) {
    return `${Math.round(n / 1000)}k`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  }
  return String(n);
};

const buildYAxisScale = (dataMax, targetTickCount = 5) => {
  if (dataMax <= 0) {
    return { maxValue: 1, yTicks: [0, 1] };
  }

  const roughStep = dataMax / targetTickCount;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  let niceStep = magnitude;

  if (normalized <= 1) {
    niceStep = magnitude;
  } else if (normalized <= 2) {
    niceStep = 2 * magnitude;
  } else if (normalized <= 5) {
    niceStep = 5 * magnitude;
  } else {
    niceStep = 10 * magnitude;
  }

  const topTick = Math.ceil(dataMax / niceStep) * niceStep;
  const yTicks = [];
  for (let value = 0; value <= topTick; value += niceStep) {
    yTicks.push(value);
  }

  return { maxValue: topTick, yTicks };
};

const buildAnimalYearBarChart = (summary) => {
  if (!summary) {
    return { categories: [], maxValue: 1, yTicks: [0, 1] };
  }

  const categories = ANIMAL_KEYS.map(({ key, label }) => ({
    key,
    label: animalLabelShort(label),
    current: Math.round(Number(summary[key]) || 0),
    previous: Math.round(Number(summary[`previous_${key}`]) || 0),
  }));

  let dataMax = Math.max(
    0,
    ...categories.flatMap((category) => [category.current, category.previous])
  );
  if (dataMax <= 0) {
    dataMax = Math.max(summary.total ?? 0, summary.previous_total ?? 0);
  }
  const { maxValue, yTicks } = buildYAxisScale(dataMax);

  return { categories, maxValue, yTicks };
};

const formatBarValueLabel = (value) => {
  const n = Math.round(Number(value) || 0);
  if (n >= 1000) {
    return formatAxisTick(n);
  }
  return formatInt(n);
};

const getBarValueLabelY = (barTopY, padTopLimit) => Math.max(padTopLimit, barTopY - 4);

const MOBILE_CHART_MEDIA = "(max-width: 768px)";

const CHART_LAYOUT_DESKTOP = {
  width: 640,
  height: 320,
  padLeft: 48,
  padRight: 12,
  padTop: 14,
  padBottom: 40,
};

const CHART_LAYOUT_MOBILE = {
  width: 360,
  height: 400,
  padLeft: 40,
  padRight: 8,
  padTop: 12,
  padBottom: 46,
};

const getChartLayout = () =>
  typeof window !== "undefined" && window.matchMedia(MOBILE_CHART_MEDIA).matches
    ? CHART_LAYOUT_MOBILE
    : CHART_LAYOUT_DESKTOP;

function useChartLayout() {
  const [layout, setLayout] = useState(() => getChartLayout());

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_CHART_MEDIA);
    const syncLayout = () => setLayout(getChartLayout());
    syncLayout();
    mediaQuery.addEventListener("change", syncLayout);
    return () => mediaQuery.removeEventListener("change", syncLayout);
  }, []);

  return layout;
}

function AnimalYearComparisonBarChart({
  summary,
  currentYearLabel,
  previousYearLabel,
  zilaOptions,
  selectedBarZila,
  onBarZilaChange,
  isBarChartLoading,
  barChartError,
}) {
  const { categories, maxValue, yTicks } = useMemo(() => buildAnimalYearBarChart(summary), [summary]);
  const chartLayout = useChartLayout();
  const { width, height, padLeft, padRight, padTop, padBottom } = chartLayout;

  const hasChartableData = useMemo(() => {
    if (!summary) {
      return false;
    }
    return (
      categories.some((category) => category.current > 0 || category.previous > 0) ||
      (summary.previous_total ?? 0) > 0 ||
      (summary.total ?? 0) > 0
    );
  }, [categories, summary]);
  const selectedZilaLabel =
    selectedBarZila === "all"
      ? "All zilas"
      : zilaOptions.find((option) => option.value === selectedBarZila)?.label || selectedBarZila;

  if (!hasChartableData && !isBarChartLoading) {
    return (
      <div className="animal-year-bar-card">
        <div className="animal-year-bar-head">
          <div className="animal-year-bar-head-text">
            <h3 className="animal-year-bar-title">Hides counts by year</h3>
            <p className="animal-year-bar-subtitle">
              {currentYearLabel} vs {previousYearLabel}
            </p>
          </div>
          <BarChartZilaFilter
            zilaOptions={zilaOptions}
            selectedBarZila={selectedBarZila}
            onBarZilaChange={onBarZilaChange}
            disabled={isBarChartLoading}
          />
        </div>
        {barChartError ? (
          <p className="dashboard-error-message" role="alert">
            {barChartError}
          </p>
        ) : (
          <>
            <BarChartAnimalTotals summary={summary} isLoading={isBarChartLoading} />
            <p className="animal-year-bar-empty">No hide counts to chart for {selectedZilaLabel}.</p>
          </>
        )}
      </div>
    );
  }

  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;
  const groupCount = categories.length;
  const groupW = plotW / groupCount;
  const barGap = 8;
  const barW = Math.min(32, (groupW - barGap * 3) / 2);
  const groupInnerOffset = (groupW - barW * 2 - barGap) / 2;

  const barHeight = (value) => (value / maxValue) * plotH;
  const baselineY = padTop + plotH;
  const valueLabelMinY = padTop + 10;
  const zeroBarHeight = 3;

  const getBarRect = (value) => {
    if (value > 0) {
      const height = barHeight(value);
      return { y: baselineY - height, height };
    }
    return { y: baselineY - zeroBarHeight, height: zeroBarHeight };
  };

  return (
    <div className="animal-year-bar-card">
      <div className="animal-year-bar-head">
        <div className="animal-year-bar-head-text">
          <h3 className="animal-year-bar-title">Hides counts by year</h3>
          <p className="animal-year-bar-subtitle">
            {currentYearLabel} vs {previousYearLabel}
            {selectedBarZila !== "all" ? ` · ${selectedZilaLabel}` : ""}
          </p>
        </div>
        <div className="animal-year-bar-head-actions">
          <BarChartZilaFilter
            zilaOptions={zilaOptions}
            selectedBarZila={selectedBarZila}
            onBarZilaChange={onBarZilaChange}
            disabled={isBarChartLoading}
          />
          <ul className="animal-year-bar-legend" aria-hidden="true">
          <li className="animal-year-bar-legend-item">
            <span className="animal-year-bar-legend-swatch animal-year-bar-legend-swatch--current" />
            <span>{currentYearLabel}</span>
          </li>
          <li className="animal-year-bar-legend-item">
            <span className="animal-year-bar-legend-swatch animal-year-bar-legend-swatch--previous" />
            <span>{previousYearLabel}</span>
          </li>
        </ul>
        </div>
      </div>
      {barChartError ? (
        <p className="dashboard-error-message animal-year-bar-inline-error" role="alert">
          {barChartError}
        </p>
      ) : null}
      <div className="animal-year-bar-chart-wrap">
        {isBarChartLoading ? <p className="animal-year-bar-loading">Updating chart…</p> : null}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          className="animal-year-bar-svg"
          role="img"
          aria-label={categories
            .map(
              (category) =>
                `${category.label}: ${currentYearLabel} ${formatInt(category.current)}, ${previousYearLabel} ${formatInt(category.previous)}`
            )
            .join("; ")}
        >
          <line
            x1={padLeft}
            y1={padTop}
            x2={padLeft}
            y2={baselineY}
            className="animal-year-bar-axis-line"
          />
          {yTicks.map((tick) => {
            const y = baselineY - barHeight(tick);
            return (
              <g key={`tick-${tick}`}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={width - padRight}
                  y2={y}
                  className="animal-year-bar-grid-line"
                />
                <text x={padLeft - 6} y={y + 4} className="animal-year-bar-axis-label" textAnchor="end">
                  {formatAxisTick(tick)}
                </text>
              </g>
            );
          })}
          <line
            x1={padLeft}
            y1={baselineY}
            x2={width - padRight}
            y2={baselineY}
            className="animal-year-bar-axis-line"
          />
          {categories.map((category, index) => {
            const groupX = padLeft + index * groupW;
            const currentX = groupX + groupInnerOffset;
            const previousX = currentX + barW + barGap;
            const labelX = groupX + groupW / 2;
            const currentRect = getBarRect(category.current);
            const previousRect = getBarRect(category.previous);
            const currentLabelX = currentX + barW / 2;
            const previousLabelX = previousX + barW / 2;
            const currentValueY =
              category.current > 0
                ? getBarValueLabelY(currentRect.y, valueLabelMinY)
                : baselineY - zeroBarHeight - 4;
            const previousValueY = getBarValueLabelY(previousRect.y, valueLabelMinY);

            return (
              <g key={category.key}>
                <rect
                  x={currentX}
                  y={currentRect.y}
                  width={barW}
                  height={currentRect.height}
                  rx={4}
                  className={`animal-year-bar-rect animal-year-bar-rect--current${category.current <= 0 ? " animal-year-bar-rect--zero" : ""}`}
                />
                <rect
                  x={previousX}
                  y={previousRect.y}
                  width={barW}
                  height={previousRect.height}
                  rx={4}
                  className={`animal-year-bar-rect animal-year-bar-rect--previous${category.previous <= 0 ? " animal-year-bar-rect--zero" : ""}`}
                />
                <text
                  x={currentLabelX}
                  y={currentValueY}
                  className="animal-year-bar-value-label animal-year-bar-value-label--current"
                  textAnchor="middle"
                >
                  {formatBarValueLabel(category.current)}
                </text>
                <text
                  x={previousLabelX}
                  y={previousValueY}
                  className="animal-year-bar-value-label animal-year-bar-value-label--previous"
                  textAnchor="middle"
                >
                  {formatBarValueLabel(category.previous)}
                </text>
                <text x={labelX} y={baselineY + 16} className="animal-year-bar-category-label" textAnchor="middle">
                  {category.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {!barChartError ? (
        <BarChartAnimalTotals summary={summary} isLoading={isBarChartLoading} />
      ) : null}
    </div>
  );
}

const formatDeltaCompact = (current, previous) => {
  if (previous <= 0) {
    if (current <= 0) {
      return null;
    }
    return { arrow: "▲", text: "100.0%", tone: "positive" };
  }
  const raw = ((current - previous) / previous) * 100;
  const absVal = Math.abs(raw);
  const formatted = absVal >= 10 ? absVal.toFixed(1) : absVal.toFixed(2);
  if (raw < 0) {
    return { arrow: "▼", text: `${formatted}%`, tone: "negative" };
  }
  if (raw > 0) {
    return { arrow: "▲", text: `${formatted}%`, tone: "positive" };
  }
  return { arrow: "", text: "0%", tone: "neutral" };
};

function BarChartAnimalTotals({ summary, isLoading }) {
  if (!summary) {
    return null;
  }

  return (
    <div
      className={`hide-breakdown-animal-grid animal-year-bar-animal-grid${isLoading ? " animal-year-bar-animal-grid--loading" : ""}`}
      aria-label="Hide counts by animal for selected chart filters"
    >
      {ANIMAL_KEYS.map(({ key, label }) => (
        <article key={key} className="hide-breakdown-animal-card">
          <span className="hide-breakdown-animal-name">{label}</span>
          <p className="hide-breakdown-animal-current">{formatInt(summary[key] ?? 0)}</p>
        </article>
      ))}
    </div>
  );
}

function BarChartZilaFilter({ zilaOptions, selectedBarZila, onBarZilaChange, disabled }) {
  return (
    <div className="animal-year-bar-zila-filter">
      <label htmlFor="bar-chart-zila-filter" className="score-filter-label">
        Zila
      </label>
      <select
        id="bar-chart-zila-filter"
        value={selectedBarZila}
        onChange={(event) => onBarZilaChange(event.target.value)}
        disabled={disabled}
        className="score-filter-select animal-year-bar-zila-select"
      >
        <option value="all">All zilas</option>
        {zilaOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const shortfallVsLastYear = (current, previous) => {
  if (previous <= 0) {
    return null;
  }
  const pct = (1 - current / previous) * 100;
  if (pct <= 0) {
    return null;
  }
  return { arrow: "▼", text: `${pct >= 10 ? pct.toFixed(1) : pct.toFixed(2)}% shortfall` };
};

function DashboardOverviewPage() {
  const navigate = useNavigate();
  const userRole = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY));
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isExecutive = userRole === "EXECUTIVE";
  const showHideBreakdown = isSuperAdmin || isExecutive;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [summary, setSummary] = useState(null);
  const [zilaRows, setZilaRows] = useState([]);
  const [selectedSeasonYear, setSelectedSeasonYear] = useState(String(SCORECARD_LIVE_SEASON_YEAR));
  const [seasonYearOptions, setSeasonYearOptions] = useState(() => [...DEFAULT_SCORECARD_SEASON_YEARS]);
  const [activeComparisonYear, setActiveComparisonYear] = useState(null);
  const [selectedEidDay, setSelectedEidDay] = useState("all");
  const [barChartSummary, setBarChartSummary] = useState(null);
  const [isBarChartLoading, setIsBarChartLoading] = useState(false);
  const [barChartError, setBarChartError] = useState("");
  const [selectedBarZila, setSelectedBarZila] = useState("all");
  const [barChartComparisonYear, setBarChartComparisonYear] = useState(null);

  const loadScorecard = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");
      const queryString = getScorecardQueryString({
        year: selectedSeasonYear,
        eidDay: selectedEidDay,
      });
      const response = await fetch(
        `${getBaseApiUrl()}${getScorecardEndpoint(userRole)}${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const payload = await parsePayload(response);
      if (!response.ok) {
        const availableYears = extractAvailableYears(payload);
        if (availableYears.length > 0) {
          setSeasonYearOptions((prev) => mergeSeasonYearOptions(prev, availableYears));
        }
        throw new Error(
          getScorecardErrorMessage(payload, "Unable to load hide breakdown right now.")
        );
      }

      const data = extractScorecardData(payload);
      const comparisonYear = getComparisonYearFromScorecardData(data);
      setActiveComparisonYear(comparisonYear);
      setSeasonYearOptions((prev) =>
        mergeSeasonYearOptions(prev, extractAvailableYears(payload), DEFAULT_SCORECARD_SEASON_YEARS)
      );
      setSummary(scorecardSummaryFromPayload(data));
      setBarChartSummary(null);
      setBarChartError("");
      setZilaRows(toScoreRows(data?.zila_wise || data?.zilaWise, "Zila"));
    } catch (error) {
      setErrorMessage(error.message || "Unable to load hide breakdown right now.");
      setSummary(null);
      setBarChartSummary(null);
      setZilaRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, selectedEidDay, selectedSeasonYear, userRole]);

  const loadBarChartScorecard = useCallback(async () => {
    if (!summary) {
      return;
    }

    if (selectedBarZila === "all") {
      setBarChartSummary(summary);
      setBarChartComparisonYear(activeComparisonYear);
      setBarChartError("");
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsBarChartLoading(true);
      setBarChartError("");
      const queryString = getScorecardQueryString({
        year: selectedSeasonYear,
        eidDay: selectedEidDay,
        zila: selectedBarZila,
      });
      const response = await fetch(
        `${getBaseApiUrl()}${getScorecardEndpoint(userRole)}${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const payload = await parsePayload(response);
      if (!response.ok) {
        throw new Error(
          getScorecardErrorMessage(payload, "Unable to load bar chart for this zila right now.")
        );
      }

      const data = extractScorecardData(payload);
      setBarChartComparisonYear(
        getComparisonYearFromScorecardData(data, { zila: selectedBarZila }) ?? activeComparisonYear
      );
      setBarChartSummary(scorecardSummaryFromPayload(data, { zila: selectedBarZila }));
    } catch (error) {
      setBarChartError(error.message || "Unable to load bar chart for this zila right now.");
      setBarChartSummary(null);
    } finally {
      setIsBarChartLoading(false);
    }
  }, [
    activeComparisonYear,
    navigate,
    selectedBarZila,
    selectedEidDay,
    selectedSeasonYear,
    summary,
    userRole,
  ]);

  const isLiveSeason = isLiveSeasonYear(selectedSeasonYear);

  const seasonYearSelectOptions = useMemo(
    () => mergeSeasonYearOptions(seasonYearOptions, selectedSeasonYear),
    [seasonYearOptions, selectedSeasonYear]
  );

  const handleSeasonYearChange = (event) => {
    setSelectedSeasonYear(event.target.value);
  };

  useEffect(() => {
    if (!showHideBreakdown) {
      return;
    }
    loadScorecard();
  }, [showHideBreakdown, loadScorecard]);

  useEffect(() => {
    if (!showHideBreakdown || !summary) {
      return;
    }
    loadBarChartScorecard();
  }, [showHideBreakdown, summary, loadBarChartScorecard]);

  if (!showHideBreakdown) {
    return (
      <section className="dashboard-card">
        <p className="dashboard-card-subtitle">Welcome to your dashboard.</p>
      </section>
    );
  }

  const currentTotal = summary?.total ?? 0;
  const previousTotal = summary?.previous_total ?? 0;
  const totalShortfall = shortfallVsLastYear(currentTotal, previousTotal);
  const totalDelta = formatDeltaCompact(currentTotal, previousTotal);

  const rankedZilas = useMemo(() => {
    return [...zilaRows].sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [zilaRows]);

  const barZilaOptions = useMemo(() => {
    const unique = new Map();
    for (const row of zilaRows) {
      const label = getZilaRowTitle(row);
      if (!label || label === "—") {
        continue;
      }
      unique.set(label, { value: label, label });
    }
    return [...unique.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [zilaRows]);

  const comparisonYearLabel = activeComparisonYear ? String(activeComparisonYear) : "prior season";
  const seasonYearLabel = formatSeasonYearOptionLabel(selectedSeasonYear);
  const barChartYearLabel = seasonYearLabel;
  const barChartComparisonLabel = barChartComparisonYear
    ? String(barChartComparisonYear)
    : comparisonYearLabel;
  const barChartData = barChartSummary ?? summary;

  const filterContextParts = [
    seasonYearLabel,
    selectedEidDay !== "all"
      ? EID_DAY_OPTIONS.find((o) => o.value === selectedEidDay)?.label || selectedEidDay
      : null,
  ].filter(Boolean);

  return (
    <section className="dashboard-card dashboard-overview-card">
      <div className="hide-breakdown-header">
        <h2 className="hide-breakdown-section-title">Hide breakdown</h2>
        <div className="hide-breakdown-toolbar">
          <label htmlFor="overview-season-year-filter" className="score-filter-label">
            Year
          </label>
          <select
            id="overview-season-year-filter"
            value={selectedSeasonYear}
            onChange={handleSeasonYearChange}
            disabled={isLoading}
            className="score-filter-select hide-breakdown-filter-select"
          >
            {seasonYearSelectOptions.map((year) => (
              <option key={year} value={String(year)}>
                {formatSeasonYearOptionLabel(year)}
              </option>
            ))}
          </select>
          <label htmlFor="overview-eid-day-filter" className="score-filter-label">
            Eid day
          </label>
          <select
            id="overview-eid-day-filter"
            value={selectedEidDay}
            onChange={(event) => setSelectedEidDay(event.target.value)}
            disabled={isLoading}
            className="score-filter-select hide-breakdown-filter-select"
          >
            {EID_DAY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button type="button" className="hide-breakdown-refresh" onClick={loadScorecard} disabled={isLoading}>
            {isLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="hide-breakdown-section">
        {errorMessage ? (
          <p className="dashboard-error-message" role="alert">
            {errorMessage}
          </p>
        ) : null}
        {isLoading && !summary ? <p className="hide-breakdown-loading">Loading hide breakdown…</p> : null}

        {summary ? (
          <>
            <div className="hide-breakdown-summary-grid">
              <article className="hide-breakdown-stat hide-breakdown-stat--primary">
                <p className="hide-breakdown-stat-label">Total hides collected</p>
                <p className="hide-breakdown-stat-value">{formatInt(currentTotal)}</p>
                <p className="hide-breakdown-stat-sub">
                  {isLiveSeason ? "Hides this season" : "Archived season total"}
                  {filterContextParts.length > 0 ? ` · ${filterContextParts.join(" · ")}` : ""}
                </p>
              </article>
              <article className="hide-breakdown-stat hide-breakdown-stat--muted">
                <p className="hide-breakdown-stat-label">{comparisonYearLabel}</p>
                <p className="hide-breakdown-stat-value hide-breakdown-stat-value--dark">{formatInt(previousTotal)}</p>
                {totalShortfall ? (
                  <p className="hide-breakdown-stat-sub hide-breakdown-stat-sub--danger">
                    {totalShortfall.arrow} {totalShortfall.text}
                  </p>
                ) : totalDelta && previousTotal > 0 ? (
                  <p
                    className={`hide-breakdown-stat-sub hide-breakdown-stat-sub--delta-${totalDelta.tone}`}
                  >
                    {totalDelta.arrow} {totalDelta.text} vs {comparisonYearLabel}
                  </p>
                ) : (
                  <p className="hide-breakdown-stat-sub">Compared to {comparisonYearLabel}</p>
                )}
              </article>
            </div>

            <AnimalYearComparisonBarChart
              summary={barChartData}
              currentYearLabel={barChartYearLabel}
              previousYearLabel={barChartComparisonLabel}
              zilaOptions={barZilaOptions}
              selectedBarZila={selectedBarZila}
              onBarZilaChange={setSelectedBarZila}
              isBarChartLoading={isBarChartLoading}
              barChartError={barChartError}
            />

            <section className="zila-wise-section" aria-labelledby="zila-wise-heading">
              <h3 id="zila-wise-heading" className="zila-wise-title">
                {rankedZilas.length > 0
                  ? `All ${rankedZilas.length} zilas by total hides`
                  : "Zilas by total hides"}
              </h3>
              <p className="zila-wise-subtitle">
                Ranked highest to lowest · {seasonYearLabel}
              </p>
              {!isLoading && rankedZilas.length === 0 ? (
                <p className="zila-wise-empty">No zila-wise data for this Eid filter.</p>
              ) : null}
              <ul className="zila-wise-list">
                {rankedZilas.map((row, index) => {
                  const rank = index + 1;
                  const total = row.total || 0;
                  const areaTag = getZilaAreaTag(row);

                  const rowKey = String(
                    row?.zila_id ?? row?.zilaId ?? row?.id ?? row?.zila ?? row?.label ?? `row-${rank}`
                  ).trim();

                  return (
                    <li key={`zila-${rowKey}-${rank}`} className="zila-wise-card">
                      <div className="zila-wise-card-head">
                        <div className="zila-wise-head-left">
                          <span
                            className={`zila-wise-rank-badge ${rank === 1 ? "zila-wise-rank-badge--first" : ""}`}
                            aria-label={`Rank ${rank}`}
                          >
                            {rank === 1 ? <span className="zila-wise-rank-first-mark">1</span> : `#${rank}`}
                          </span>
                          <div className="zila-wise-title-block">
                            <span className="zila-wise-name">{getZilaRowTitle(row)}</span>
                            {areaTag ? (
                              <span className="zila-wise-tag" title={areaTag}>
                                {areaTag}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="zila-wise-total-wrap">
                          <span className="zila-wise-total">{formatInt(total)}</span>
                        </div>
                      </div>
                      <div className="zila-wise-animals-box">
                        <div className="zila-wise-animals" role="list">
                          {ANIMAL_KEYS.map(({ key, label, color }) => (
                            <div key={key} className="zila-wise-animal" role="listitem">
                              <span className="zila-wise-animal-label" style={{ color }}>
                                {label}
                              </span>
                              <span className="zila-wise-animal-count" style={{ color }}>
                                {formatInt(row[key] ?? 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          </>
        ) : null}
      </div>
    </section>
  );
}

export default DashboardOverviewPage;
